import React, { useState, useEffect } from 'react';
import { moFamilyStore } from '../stores/moFamilyStore';
import type { MoFamilySlot } from '../pages/mofamily/types';

export const SlotManagementTable: React.FC<{
 familyHash: string;
 onRemoveSlot: (slotNumber: number) => void;
}> = ({ familyHash, onRemoveSlot }) => {
 const [slots, setSlots] = useState<MoFamilySlot[]>([]);
 const [fileNames, setFileNames] = useState<{ [hash: string]: string }>({});
 const [familyNames, setFamilyNames] = useState<{ [hash: string]: string }>({});
 const [editingSlotSet, setEditingSlotSet] = useState<string | null>(moFamilyStore.getState().editingSlotSet);
 // 슬롯 고유 라벨 편집 — 이 슬롯에 어떤 영상이 오든 표시할 고정 문구(위임 슬롯용).
 // 편집 중인 슬롯 번호와 입력값만 들고 있는다(행 단위 인라인 편집).
 const [editingLabelSlot, setEditingLabelSlot] = useState<number | null>(null);
 const [labelDraft, setLabelDraft] = useState('');
 const [labelPosDraft, setLabelPosDraft] = useState<string>('');
 const [savingLabel, setSavingLabel] = useState(false);

 const beginEditLabel = (slot: MoFamilySlot) => {
  setEditingLabelSlot(slot.slot_number);
  setLabelDraft(slot.label ?? '');
  setLabelPosDraft(slot.overlay_position != null ? String(slot.overlay_position) : '');
 };

 const saveLabel = async (slotNumber: number) => {
  const setName = editingSlotSet || 'A';
  setSavingLabel(true);
  try {
   const resp = await fetch(
    `/mofamily/${familyHash}/slot-sets/${setName}/slots/${slotNumber}/label`,
    {
     method: 'PUT',
     headers: { 'Content-Type': 'application/json' },
     credentials: 'include',
     body: JSON.stringify({
      label: labelDraft.trim() === '' ? null : labelDraft.trim(),
      overlay_position: labelPosDraft === '' ? null : Number(labelPosDraft),
     }),
    }
   );
   const body = await resp.json();
   if (!resp.ok || !body.success) {
    alert(body.error || '슬롯 라벨 저장에 실패했습니다.');
    return;
   }
   // 로컬 반영 — 저장 성공한 값으로만 갱신(서버가 정규화한 값을 그대로 쓴다).
   setSlots(prev => prev.map(s => s.slot_number === slotNumber
    ? { ...s, label: body.data?.label ?? null, overlay_position: body.data?.overlay_position ?? null }
    : s));
   setEditingLabelSlot(null);
  } catch {
   alert('슬롯 라벨 저장에 실패했습니다.');
  } finally {
   setSavingLabel(false);
  }
 };

 // Subscribe to editingSlotSet changes
 useEffect(() => {
 const unsubscribe = moFamilyStore.subscribe((state) => {
 if (state.editingSlotSet !== editingSlotSet) {
 setEditingSlotSet(state.editingSlotSet);
 }
 });
 return () => unsubscribe();
 }, [editingSlotSet]);

 useEffect(() => {
 const updateSlots = () => {
 const store = moFamilyStore.getState();
 const slotSets = store.slotSets.get(familyHash);
 if (slotSets) {
 // Get the current editing set or the active set
 const editingSetName = store.editingSlotSet || 'A';
 const targetSet = slotSets.find(s => s.setName === editingSetName) ||
 slotSets.find(s => s.isActive) ||
 slotSets[0];
 if (targetSet && Array.isArray(targetSet.slots)) {
 setSlots(targetSet.slots);
 } else {
 setSlots([]);
 }
 } else {
 setSlots([]);
 }
 };

 updateSlots();

 const unsubscribe = moFamilyStore.subscribe(updateSlots);
 return () => unsubscribe();
 }, [familyHash, editingSlotSet]);

 // Load file names for file hashes
 useEffect(() => {
 const loadFileNames = async () => {
 const uniqueFileHashes = [...new Set(slots.filter(s => s.file_hash).map(s => s.file_hash!))];
 if (uniqueFileHashes.length === 0) return;

 try {
 const response = await fetch('/mofile/my-files', {
 credentials: 'include'
 });
 if (response.ok) {
 const data = await response.json();
 const nameMap: { [hash: string]: string } = {};
 data.data.forEach((file: any) => {
 if (uniqueFileHashes.includes(file.file_hash)) {
 nameMap[file.file_hash] = file.file_name;
 }
 });
 setFileNames(nameMap);
 }
 } catch (error) {
 console.error('Error loading file names:', error);
 }
 };

 loadFileNames();
 }, [slots]);

 // Load family names for authorized families
 useEffect(() => {
 const loadFamilyNames = async () => {
 const uniqueFamilyHashes = [...new Set(slots.flatMap(s => s.authorized_families || []))];
 if (uniqueFamilyHashes.length === 0) return;

 const nameMap: { [hash: string]: string } = {};

 // First check if current family is in the list
 const currentFamily = moFamilyStore.getState().families.get(familyHash);
 if (currentFamily && uniqueFamilyHashes.includes(familyHash)) {
 nameMap[familyHash] = currentFamily.name;
 }

 // Check all families in the store
 const allFamilies = moFamilyStore.getState().families;
 allFamilies.forEach((family, hash) => {
 if (uniqueFamilyHashes.includes(hash)) {
 nameMap[hash] = family.name;
 }
 });

 // Load names from hierarchy or relationships
 const hierarchyNodes = moFamilyStore.getState().hierarchy;
 const findInHierarchy = (nodes: any[]): void => {
 nodes.forEach(node => {
 if (uniqueFamilyHashes.includes(node.moFamilyHash)) {
 nameMap[node.moFamilyHash] = node.name;
 }
 if (node.children) {
 findInHierarchy(node.children);
 }
 });
 };
 findInHierarchy(hierarchyNodes);

 // For any remaining hashes without names, try to fetch from API
 const missingHashes = uniqueFamilyHashes.filter(hash => !nameMap[hash]);
 if (missingHashes.length > 0) {
 try {
 const promises = missingHashes.map(async (hash) => {
 try {
 const response = await fetch(`/mofamily/${hash}`, {
 credentials: 'include'
 });
 if (response.ok) {
 const data = await response.json();
 if (data.success && data.data) {
 nameMap[hash] = data.data.name;
 }
 }
 } catch (err) {
 console.error(`Error fetching family info for ${hash}:`, err);
 }
 });

 await Promise.all(promises);
 } catch (error) {
 console.error('Error fetching family names:', error);
 }
 }

 setFamilyNames(nameMap);
 };

 loadFamilyNames();
 }, [slots, familyHash]);

 return slots.length > 0 ? (
 <div className="space-y-2">
 <div className="flex items-center justify-between mb-2">
 <h5 className="text-sm font-medium text-gray-700">
 슬롯 세트 {editingSlotSet || 'A'} 슬롯 목록
 </h5>
 </div>
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead>
 <tr className="text-left text-sm text-gray-600 border-b">
 <th className="pb-2">슬롯 번호</th>
 <th className="pb-2">재생 시간</th>
 <th className="pb-2">파일</th>
 <th className="pb-2">권한 가진 패밀리</th>
 <th className="pb-2" title="이 슬롯에 어떤 영상이 오든 표시할 고정 문구. 위임한 슬롯에 남의 영상이 들어와도 유지됩니다.">슬롯 라벨</th>
 <th className="pb-2 text-right">작업</th>
 </tr>
 </thead>
 <tbody>
 {slots.map((slot) => (
 <tr key={slot.slot_number} className="border-b hover:bg-gray-50 group">
 <td className="py-2">#{slot.slot_number}</td>
 <td className="py-2">{slot.playing_time}초</td>
 <td className="py-2">
 {slot.file_hash ? (
 <span className="text-sm text-gray-700 break-all">
 {fileNames[slot.file_hash] || `${slot.file_hash.substring(0, 20)}...`}
 </span>
 ) : (
 <span className="text-sm text-gray-400">비어있음</span>
 )}
 </td>
 <td className="py-2">
 {slot.authorized_families && slot.authorized_families.length > 0 ? (
 <span className="text-sm break-all">
 {slot.authorized_families.map(fh => familyNames[fh] || fh.substring(0, 15) + '...').join(', ')}
 </span>
 ) : (
 <span className="text-sm text-gray-400">없음</span>
 )}
 </td>
 <td className="py-2">
 {editingLabelSlot === slot.slot_number ? (
 <div className="flex items-center gap-1">
 <input
 type="text"
 value={labelDraft}
 onChange={(e) => setLabelDraft(e.target.value)}
 maxLength={24}
 placeholder="예: 근처 행사 소식"
 disabled={savingLabel}
 autoFocus
 className="border border-gray-300 px-2 py-1 text-sm w-40"
 />
 <select
 value={labelPosDraft}
 onChange={(e) => setLabelPosDraft(e.target.value)}
 disabled={savingLabel}
 title="라벨을 표시할 위치. '영상 따름'이면 영상이 정한 위치에 나옵니다."
 className="border border-gray-300 px-1 py-1 text-xs"
 >
 <option value="">영상 따름</option>
 <option value="1">상단중앙</option>
 <option value="2">좌상단</option>
 <option value="3">좌하단</option>
 <option value="4">우하단</option>
 <option value="5">우상단</option>
 </select>
 <button
 onClick={() => saveLabel(slot.slot_number)}
 disabled={savingLabel}
 className="px-2 py-1 bg-blue-500 text-white text-xs hover:bg-blue-600 disabled:opacity-50"
 >
 {savingLabel ? '저장...' : '저장'}
 </button>
 <button
 onClick={() => setEditingLabelSlot(null)}
 disabled={savingLabel}
 className="px-2 py-1 bg-gray-200 text-gray-700 text-xs hover:bg-gray-300"
 >
 취소
 </button>
 </div>
 ) : (
 <button
 onClick={() => beginEditLabel(slot)}
 className="text-left text-sm hover:text-blue-600"
 title="클릭해서 수정"
 >
 {slot.label ? (
 <>
 <span className="text-gray-800">{slot.label}</span>
 {slot.overlay_position != null && (
 <span className="ml-1 text-xs text-gray-400">
 ({['','상단중앙','좌상단','좌하단','우하단','우상단'][slot.overlay_position]})
 </span>
 )}
 </>
 ) : (
 <span className="text-gray-400">+ 라벨</span>
 )}
 </button>
 )}
 </td>
 <td className="py-2 text-right">
 {slots.length > 1 && (
 <button
 onClick={() => onRemoveSlot(slot.slot_number)}
 className="text-red-600 hover:text-red-800 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200"
 >
 삭제
 </button>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>
 ) : (
 <p className="text-gray-500 text-center py-4">슬롯 세트 {editingSlotSet || 'A'}에 슬롯이 없습니다.</p>
 );
};
